/**
 * Biblioteca de modelos SIMULADA (RNF19) -- NAO E PRODUCAO.
 *
 * Como a biblioteca nasce:
 *  1. Um catalogo FIXO de modelos escritos a mao, em pt-BR, cobrindo a regua do
 *     carrinho (T+30min na voz, T+1h, T+24h, T+48h), a de expirados (D+15 a
 *     D+90), o comercial (quem respondeu), o suporte pos-pagamento e disparos
 *     de marketing para bases DEPOIS DO FATO. Nenhum Math.random.
 *  2. TODA copy fala com quem ja abandonou o checkout ou ja teve o plano
 *     vencido (ou com quem ja respondeu / ja pagou). Nao ha lembrete antes de
 *     vencer nem aviso de reajuste para quem ainda esta em dia: sem o fato nao
 *     ha recuperacao e nao ha comissao (decisao do dono).
 *  3. Modelos GLOBAIS (`companyId: null`) sao a base operada por nos; cada
 *     empresa do catalogo tem modelos proprios, com a voz da marca dela.
 *     Duas variantes A/B (carrinho T+1h e e-mail T+24h) existem para o
 *     ranking "melhor da categoria" e a comparacao lado a lado terem o que
 *     mostrar.
 *  4. Desempenho: base de envios (30 dias, fator 100) escalada pelos dias da
 *     janela e pelo fator de envio da empresa (derivado de `feeBps` e de um
 *     hash do id; empresa em implantacao ou pausada nao envia). Cada medida e
 *     uma taxa FIXA em bps sobre a anterior. O periodo anterior aplica um
 *     desvio deterministico (hash do nome) ou explicito (`anterior`).
 *  5. So publicos que GERAM CREDITO (carrinho abandonado, plano vencido) tem
 *     `paid` > 0. Mensagem para quem ja respondeu e enviada pela vendedora: o
 *     credito continua no toque da regua que trouxe o lead (ADR-001 E5), entao
 *     aqui `paid` = 0 e `paidViaSeller` conta quem fechou com ela depois.
 *     Suporte pos-pagamento tambem nao credita (a venda ja aconteceu).
 *  6. Modelo pendente ou rejeitado tem base zero: o que nao foi aprovado nao
 *     sai.
 */

import { AGORA_SIMULADO, menosMinutos } from '../../lib/api';
import { EMPRESAS, EMPRESA_DEMO_ID, ofertasDaEmpresa } from '../../lib/mock/catalogo';
import { rangeDays } from '../../lib/period';
import type { CompanySummary, PeriodRange } from '../../types';
import type { MessageTemplate, TemplateAudience, TemplateContent, TemplatePerformance, TemplateQuery, TemplateStatus, TemplateVersion, TemplatesResponse } from './types';
import { conteudoDe } from './versoes';
import { extrairVariaveis } from './vocabulario';

const DIAS_PADRAO = 30;
const DIAS_MAXIMO = 365;
const HORA = 60;
const DIA = 24 * HORA;
const AUTOR_EQUIPE = 'Equipe Converte Fácil';

/** Hash pequeno e estavel: mesmo texto, mesmo numero, em qualquer maquina. */
export function hashSimulado(texto: string): number {
  let h = 7;
  for (let i = 0; i < texto.length; i += 1) {
    h = (h * 31 + texto.charCodeAt(i)) % 10_007;
  }
  return h;
}

/** Dias cobertos pela janela, com teto; janela nula (desde o inicio) vale 30. */
function diasDaJanela(range: PeriodRange): number {
  const dias = rangeDays(range);
  if (dias === null) {
    return DIAS_PADRAO;
  }
  return Math.max(1, Math.min(DIAS_MAXIMO, dias));
}

/** Fator de envio (0..~120): so empresa ativa envia; o resto e onboarding ou pausa. */
function fatorDeEnvio(empresa: CompanySummary): number {
  if (empresa.status !== 'ativa') {
    return 0;
  }
  return 10 + (hashSimulado(empresa.id) % 70) + Math.round(empresa.feeBps / 100);
}

/** Escala inteira e deterministica: base (30 dias, fator 100) -> janela e fator. */
function escalar(base: number, dias: number, fator: number): number {
  return Math.round((base * dias * fator) / (DIAS_PADRAO * 100));
}

function pct(valor: number, bps: number): number {
  return Math.round((valor * bps) / 10_000);
}

/** Publicos cujo pagamento e creditado ao toque (ADR-001). Os outros so informam. */
export function publicoGeraCredito(audience: TemplateAudience): boolean {
  return audience === 'checkout_abandonado' || audience === 'plano_vencido';
}

// -----------------------------------------------------------------------------
// Catalogo
// -----------------------------------------------------------------------------

/** Taxas em bps: entrega/envio, leitura/entrega, clique/entrega, resposta/entrega, pagamento/envio, vendedora/pagos. */
interface Taxas {
  readonly entrega: number;
  readonly leitura: number | null;
  readonly clique: number | null;
  readonly resposta: number;
  readonly pagamento: number;
  readonly vendedora: number;
}

interface VersaoAnterior {
  readonly atualizadoHa: number;
  readonly resumo: string;
  readonly status: TemplateStatus;
  readonly statusNote: string | null;
  /** O que era diferente naquela versao; o resto e igual ao atual. */
  readonly conteudo: Partial<TemplateContent>;
}

/** Definicao de um modelo antes de ganhar id, variaveis, desempenho e versoes. */
type Definicao = Omit<MessageTemplate, 'id' | 'variables' | 'performance' | 'previousPerformance' | 'versions' | 'updatedAt' | 'variableMap'> & {
  /** Envios em 30 dias com fator 100. Zero para o que nao esta aprovado ou ligado. */
  readonly base: number;
  readonly taxas: Taxas;
  /** Ha quantos minutos foi alterado, relativo a AGORA_SIMULADO. */
  readonly atualizadoHa: number;
  readonly variableMap?: readonly string[];
  readonly anteriores?: readonly VersaoAnterior[];
  readonly resumoAtual?: string;
  /** Desvio explicito do periodo anterior, em % (positivo = o anterior era maior). */
  readonly anterior?: { readonly volumePct: number; readonly pagamentoPct: number };
};

const RODAPE_SAIR = 'Responda SAIR para não receber mais lembretes.';
const RODAPE_MARKETING = 'Você começou uma compra conosco. Responda SAIR para sair.';

const TAXAS_ZERO: Taxas = { entrega: 0, leitura: null, clique: null, resposta: 0, pagamento: 0, vendedora: 0 };

const CATALOGO: readonly Definicao[] = [
  // --- WhatsApp oficial · utility · régua do carrinho -------------------------
  {
    name: 'carrinho_t1h_2botoes',
    channel: 'whatsapp_oficial',
    category: 'utility',
    sector: 'funil',
    audience: 'checkout_abandonado',
    touch: 'T+1h',
    status: 'aprovado',
    statusNote: 'Aprovado pela Meta em 12/08/2026 como utility.',
    body: 'Oi, {primeiro_nome}! Vi que o seu {produto} ficou parado no checkout. Quer terminar agora?\nEstá tudo salvo do jeito que você deixou — é só tocar no botão aqui embaixo.',
    footer: RODAPE_SAIR,
    buttons: [
      { kind: 'link', label: 'Finalizar compra', url: '{link}' },
      { kind: 'reply', label: 'Tenho uma dúvida' },
    ],
    voice: null,
    companyId: null,
    usedIn: ['Régua carrinho T+1h (80% dos leads)'],
    base: 380,
    taxas: { entrega: 9_400, leitura: 7_800, clique: 1_100, resposta: 900, pagamento: 310, vendedora: 3_500 },
    atualizadoHa: 43 * DIA + 5 * HORA,
    anteriores: [
      {
        atualizadoHa: 75 * DIA,
        resumo: 'Criado',
        status: 'aprovado',
        statusNote: 'Aprovado pela Meta em 11/07/2026 como utility.',
        conteudo: { buttons: [{ kind: 'link', label: 'Finalizar compra', url: '{link}' }] },
      },
    ],
    resumoAtual: 'Botões: "Tenho uma dúvida" acrescentado — a resposta cai com a vendedora',
    anterior: { volumePct: -8, pagamentoPct: -12 },
  },
  {
    name: 'carrinho_t1h_variante_pix',
    channel: 'whatsapp_oficial',
    category: 'utility',
    sector: 'funil',
    audience: 'checkout_abandonado',
    touch: 'T+1h',
    status: 'aprovado',
    statusNote: 'Aprovado pela Meta em 02/09/2026 como utility. Em teste A/B com carrinho_t1h_2botoes.',
    body: 'Oi, {primeiro_nome}. Seu pedido do {produto} ficou aberto no checkout.\nSe o cartão recusou, dá pra concluir por PIX pelo mesmo botão, em menos de um minuto.',
    footer: RODAPE_SAIR,
    buttons: [
      { kind: 'link', label: 'Concluir pedido', url: '{link}' },
      { kind: 'reply', label: 'Tenho uma dúvida' },
    ],
    voice: null,
    companyId: null,
    usedIn: ['Régua carrinho T+1h · teste A/B (20% dos leads)'],
    base: 95,
    taxas: { entrega: 9_400, leitura: 7_900, clique: 1_300, resposta: 1_200, pagamento: 360, vendedora: 3_000 },
    atualizadoHa: 22 * DIA + 2 * HORA,
  },
  {
    name: 'carrinho_t24h_reservado',
    channel: 'whatsapp_oficial',
    category: 'utility',
    sector: 'funil',
    audience: 'checkout_abandonado',
    touch: 'T+24h',
    status: 'aprovado',
    statusNote: 'Aprovado pela Meta em 12/08/2026 como utility.',
    body: 'Oi, {primeiro_nome}! O seu {produto} continua reservado, esperando só o pagamento.\nSe travou em alguma coisa — cartão, PIX ou dúvida — me fala aqui que a gente resolve. Ou conclua pelo botão.',
    footer: RODAPE_SAIR,
    buttons: [
      { kind: 'link', label: 'Finalizar compra', url: '{link}' },
      { kind: 'reply', label: 'Não consegui pagar' },
    ],
    voice: null,
    companyId: null,
    usedIn: ['Régua carrinho T+24h'],
    base: 260,
    taxas: { entrega: 9_300, leitura: 7_100, clique: 800, resposta: 1_400, pagamento: 190, vendedora: 800 },
    atualizadoHa: 43 * DIA + 4 * HORA,
  },
  {
    name: 'carrinho_t48h_ultimo_aviso',
    channel: 'whatsapp_oficial',
    category: 'utility',
    sector: 'funil',
    audience: 'checkout_abandonado',
    touch: 'T+48h',
    status: 'aprovado',
    statusNote: 'Aprovado pela Meta em 14/08/2026 como utility, na segunda tentativa (a primeira dizia "oferta").',
    body: 'Último aviso, {primeiro_nome}: vou encerrar a reserva do seu {produto}.\nSe ainda quiser, é só concluir pelo botão. Se não fizer sentido agora, tudo bem — não vou te lembrar de novo.',
    footer: RODAPE_SAIR,
    buttons: [
      { kind: 'link', label: 'Finalizar compra', url: '{link}' },
      { kind: 'reply', label: 'Não tenho interesse' },
    ],
    voice: null,
    companyId: null,
    usedIn: ['Régua carrinho T+48h'],
    base: 190,
    taxas: { entrega: 9_200, leitura: 6_600, clique: 500, resposta: 700, pagamento: 110, vendedora: 3_000 },
    atualizadoHa: 41 * DIA + 2 * HORA,
    anteriores: [
      {
        atualizadoHa: 42 * DIA,
        resumo: 'Criado',
        status: 'rejeitado',
        statusNote: 'Rejeitado pela Meta em 13/08/2026: "última oferta" caracteriza marketing.',
        conteudo: { body: 'Última oferta, {primeiro_nome}: vou encerrar a reserva do seu {produto}.\nSe ainda quiser, é só concluir pelo botão.' },
      },
    ],
    resumoAtual: 'Texto: "oferta" trocado por "aviso" para seguir como utility',
    anterior: { volumePct: 4, pagamentoPct: 48 },
  },

  // --- WhatsApp oficial · utility · régua de expirados -----------------------
  {
    name: 'expirados_d15_retomar',
    channel: 'whatsapp_oficial',
    category: 'utility',
    sector: 'funil',
    audience: 'plano_vencido',
    touch: 'D+15',
    status: 'aprovado',
    statusNote: 'Aprovado pela Meta em 20/08/2026 como utility.',
    body: 'Oi, {primeiro_nome}! Faz 15 dias que seu acesso ao {plano} venceu.\nSeu progresso continua exatamente como você deixou — dá pra retomar sem recomeçar nada.',
    footer: RODAPE_SAIR,
    buttons: [
      { kind: 'link', label: 'Reativar acesso', url: '{link}' },
      { kind: 'reply', label: 'Tenho uma dúvida' },
    ],
    voice: null,
    companyId: null,
    usedIn: ['Régua expirados D+15'],
    base: 120,
    taxas: { entrega: 9_300, leitura: 7_000, clique: 700, resposta: 800, pagamento: 90, vendedora: 2_500 },
    atualizadoHa: 35 * DIA + 7 * HORA,
  },
  {
    name: 'expirados_d30_um_mes',
    channel: 'whatsapp_oficial',
    category: 'utility',
    sector: 'funil',
    audience: 'plano_vencido',
    touch: 'D+30',
    status: 'pendente',
    statusNote: 'Enviado à Meta em 22/09/2026. A análise costuma levar até 24 h.',
    body: 'Oi, {primeiro_nome}! Seu acesso ao {plano} venceu há um mês.\nTudo continua salvo aqui. Se voltar, você retoma de onde parou.',
    footer: RODAPE_SAIR,
    buttons: [
      { kind: 'link', label: 'Reativar acesso', url: '{link}' },
      { kind: 'reply', label: 'Falar com alguém' },
    ],
    voice: null,
    companyId: null,
    usedIn: ['Régua expirados D+30'],
    base: 0,
    taxas: TAXAS_ZERO,
    atualizadoHa: 2 * DIA + 3 * HORA,
  },
  {
    name: 'expirados_d60_dois_meses',
    channel: 'whatsapp_oficial',
    category: 'utility',
    sector: 'funil',
    audience: 'plano_vencido',
    touch: 'D+60',
    status: 'aprovado',
    statusNote: 'Aprovado pela Meta em 20/08/2026 como utility.',
    body: 'Oi, {primeiro_nome}, dois meses desde que seu acesso venceu.\nQuem para dois meses e volta consegue recuperar o ritmo. Seu acesso ao {plano} está aqui quando quiser retomar.',
    footer: RODAPE_SAIR,
    buttons: [{ kind: 'link', label: 'Reativar acesso', url: '{link}' }],
    voice: null,
    companyId: null,
    usedIn: ['Régua expirados D+60'],
    base: 70,
    taxas: { entrega: 9_100, leitura: 6_400, clique: 500, resposta: 300, pagamento: 60, vendedora: 1_000 },
    atualizadoHa: 35 * DIA + 6 * HORA,
  },
  {
    name: 'expirados_d90_ultimo_contato',
    channel: 'whatsapp_oficial',
    category: 'utility',
    sector: 'funil',
    audience: 'plano_vencido',
    touch: 'D+90',
    status: 'rejeitado',
    statusNote: 'Rejeitado pela Meta em 19/09/2026: "melhor condição" caracteriza oferta, e oferta é marketing. Reenviar como marketing ou remover a promessa.',
    body: 'Oi, {primeiro_nome}! Faz 3 meses que seu acesso ao {plano} venceu e este é meu último contato sobre isso.\nSe quiser retomar, me responde aqui que eu vejo a melhor condição pra você.',
    footer: RODAPE_SAIR,
    buttons: [{ kind: 'reply', label: 'Quero retomar' }],
    voice: null,
    companyId: null,
    usedIn: ['Régua expirados D+90'],
    base: 0,
    taxas: TAXAS_ZERO,
    atualizadoHa: 5 * DIA + 1 * HORA,
  },

  // --- WhatsApp oficial · marketing · disparos para bases depois do fato -----
  {
    name: 'abandono_turma_nova_aberta',
    channel: 'whatsapp_oficial',
    category: 'marketing',
    sector: 'comercial',
    audience: 'checkout_abandonado',
    touch: null,
    status: 'aprovado',
    statusNote: 'Aprovado pela Meta em 02/09/2026 como marketing.',
    body: 'Oi, {primeiro_nome}! Você chegou a abrir o checkout do {produto} e não concluiu.\nAbriu turma nova, e quem já tinha começado a inscrição entra primeiro. Garanta a sua pelo botão.',
    footer: RODAPE_MARKETING,
    buttons: [
      { kind: 'link', label: 'Garantir vaga', url: '{link}' },
      { kind: 'reply', label: 'Tenho uma dúvida' },
    ],
    voice: null,
    companyId: null,
    usedIn: ['Disparo "Abandonaram há mais de 48h"'],
    base: 420,
    taxas: { entrega: 9_100, leitura: 6_000, clique: 900, resposta: 500, pagamento: 70, vendedora: 2_000 },
    atualizadoHa: 22 * DIA + 9 * HORA,
  },
  {
    name: 'expirados_turma_de_retorno',
    channel: 'whatsapp_oficial',
    category: 'marketing',
    sector: 'comercial',
    audience: 'plano_vencido',
    touch: null,
    status: 'pendente',
    statusNote: 'Enviado à Meta em 23/09/2026 como marketing.',
    body: 'Oi, {primeiro_nome}. Seu acesso ao {plano} venceu, e a turma de retorno começa na segunda.\nÉ para quem parou e quer voltar com acompanhamento. As vagas são limitadas.',
    footer: RODAPE_MARKETING,
    buttons: [
      { kind: 'link', label: 'Quero voltar', url: '{link}' },
      { kind: 'reply', label: 'Tenho uma dúvida' },
    ],
    voice: null,
    companyId: null,
    usedIn: ['2 disparos agendados (expirados D+30 sem voltar)'],
    base: 0,
    taxas: TAXAS_ZERO,
    atualizadoHa: 1 * DIA + 4 * HORA,
  },

  // --- WhatsApp Evolution · texto livre --------------------------------------
  {
    name: 'comercial_retorno_duvida',
    channel: 'whatsapp_evolution',
    category: 'utility',
    sector: 'comercial',
    audience: 'respondeu_regua',
    touch: 'Após a resposta',
    status: 'texto_livre',
    statusNote: 'Enviado pela vendedora. A comissão continua creditada ao toque da régua que trouxe o lead (ADR-001 E5).',
    body: 'Oi, {primeiro_nome}, aqui é a {vendedora}, do comercial. Vi que você respondeu sobre o {produto}.\nMe conta o que travou — cartão, PIX ou dúvida sobre o conteúdo — que eu resolvo com você agora.\nSe preferir concluir direto: {link}',
    footer: null,
    buttons: [],
    voice: null,
    companyId: null,
    usedIn: ['Resposta do comercial ao "Tenho uma dúvida"'],
    base: 140,
    taxas: { entrega: 9_800, leitura: null, clique: 1_800, resposta: 5_200, pagamento: 1_400, vendedora: 10_000 },
    atualizadoHa: 12 * DIA + 2 * HORA,
  },
  {
    name: 'comercial_resposta_sem_fechar',
    channel: 'whatsapp_evolution',
    category: 'utility',
    sector: 'comercial',
    audience: 'respondeu_regua',
    touch: 'Resposta sem venda',
    status: 'texto_livre',
    statusNote: 'Enviado pela vendedora 48 h depois de uma conversa sem venda. Crédito continua no toque da régua.',
    body: 'Oi, {primeiro_nome}, é a {vendedora} de novo. A gente conversou sobre o {produto} e acabou não fechando.\nFicou alguma dúvida que eu possa resolver? Se preferir concluir sozinho: {link}',
    footer: null,
    buttons: [],
    voice: null,
    companyId: null,
    usedIn: ['Disparo "Responderam e a vendedora não fechou"'],
    base: 60,
    taxas: { entrega: 9_800, leitura: null, clique: 1_200, resposta: 3_100, pagamento: 900, vendedora: 10_000 },
    atualizadoHa: 7 * DIA + 5 * HORA,
  },
  {
    name: 'suporte_pix_expirado',
    channel: 'whatsapp_evolution',
    category: 'utility',
    sector: 'suporte',
    audience: 'checkout_abandonado',
    touch: 'T+30min',
    status: 'texto_livre',
    statusNote: null,
    body: 'Oi, {primeiro_nome}! O PIX do seu {produto} ({valor}) expirou antes de ser pago.\nGerei um novo pra você, válido por 30 minutos: {link}',
    footer: null,
    buttons: [],
    voice: null,
    companyId: null,
    usedIn: ['Checkout com PIX expirado (automático)'],
    base: 60,
    taxas: { entrega: 9_700, leitura: null, clique: 2_600, resposta: 800, pagamento: 900, vendedora: 1_000 },
    atualizadoHa: 9 * DIA + 11 * HORA,
  },
  {
    name: 'suporte_acesso_liberado',
    channel: 'whatsapp_evolution',
    category: 'utility',
    sector: 'suporte',
    audience: 'pagou_apos_toque',
    touch: 'Após o pagamento',
    status: 'texto_livre',
    statusNote: null,
    body: 'Pronto, {primeiro_nome}! Pagamento confirmado e seu acesso ao {produto} já está liberado.\nEntre por aqui: {link}\nQualquer coisa, é só responder esta mensagem.',
    footer: null,
    buttons: [],
    voice: null,
    companyId: null,
    usedIn: ['Pós-venda de quem voltou pela régua'],
    base: 95,
    taxas: { entrega: 9_800, leitura: null, clique: 7_200, resposta: 600, pagamento: 0, vendedora: 0 },
    atualizadoHa: 18 * DIA + 6 * HORA,
  },

  // --- E-mail ------------------------------------------------------------------
  {
    name: 'email_carrinho_t1h',
    channel: 'email',
    category: 'utility',
    sector: 'funil',
    audience: 'checkout_abandonado',
    touch: 'T+1h',
    status: 'texto_livre',
    statusNote: null,
    subject: '{primeiro_nome}, seu {produto} ficou parado no checkout',
    preheader: 'Dá pra terminar em menos de um minuto, sem preencher nada de novo.',
    body: 'Oi, {primeiro_nome}!\nVi que você começou a finalizar o {produto} e o pagamento não chegou a ser concluído. Está tudo salvo do jeito que você deixou.\nSe travou alguma coisa no caminho (cartão recusado, PIX que não gerou, dúvida sobre o que está incluso), é só voltar pelo botão abaixo.\nSe preferir, copie este endereço: {link}',
    footer: null,
    buttons: [{ kind: 'link', label: 'Finalizar minha compra', url: '{link}' }],
    voice: null,
    companyId: null,
    usedIn: ['Régua carrinho T+1h (sai junto do WhatsApp)'],
    base: 380,
    taxas: { entrega: 9_700, leitura: 2_800, clique: 350, resposta: 60, pagamento: 45, vendedora: 0 },
    atualizadoHa: 40 * DIA + 3 * HORA,
  },
  {
    name: 'email_carrinho_t24h',
    channel: 'email',
    category: 'utility',
    sector: 'funil',
    audience: 'checkout_abandonado',
    touch: 'T+24h',
    status: 'texto_livre',
    statusNote: null,
    subject: 'Ainda dá tempo de garantir o seu {produto}',
    preheader: 'Seu pedido continua reservado, esperando só o pagamento.',
    body: 'Oi, {primeiro_nome}!\nSeu {produto} continua reservado aqui, esperando só o pagamento — ninguém perde o lugar por ter parado no meio.\nNa maioria das vezes o que segura é uma dúvida simples: forma de pagamento, o que está incluso, como funciona o acesso. Tudo isso está na própria página do checkout.\nEndereço direto: {link}',
    footer: null,
    buttons: [{ kind: 'link', label: 'Retomar de onde parei', url: '{link}' }],
    voice: null,
    companyId: null,
    usedIn: ['Régua carrinho T+24h (sai junto do WhatsApp)'],
    base: 260,
    taxas: { entrega: 9_700, leitura: 2_400, clique: 260, resposta: 40, pagamento: 30, vendedora: 0 },
    atualizadoHa: 40 * DIA + 2 * HORA,
  },
  {
    name: 'email_carrinho_t48h',
    channel: 'email',
    category: 'utility',
    sector: 'funil',
    audience: 'checkout_abandonado',
    touch: 'T+48h',
    status: 'texto_livre',
    statusNote: null,
    subject: 'Último aviso sobre o seu {produto}, {primeiro_nome}',
    preheader: 'Depois deste e-mail eu encerro o pedido e paro de te avisar.',
    body: 'Oi, {primeiro_nome}!\nEste é o último lembrete sobre o {produto} que ficou no checkout. Depois dele eu encerro o seu pedido e paro de te avisar.\nSe ainda faz sentido pra você, é só concluir no botão aqui embaixo. Se não faz, pode ignorar este e-mail — sem ressentimento.\nEndereço direto: {link}',
    footer: null,
    buttons: [{ kind: 'link', label: 'Concluir minha compra', url: '{link}' }],
    voice: null,
    companyId: null,
    usedIn: ['Régua carrinho T+48h (sai junto do WhatsApp)'],
    base: 190,
    taxas: { entrega: 9_700, leitura: 2_600, clique: 220, resposta: 30, pagamento: 20, vendedora: 0 },
    atualizadoHa: 40 * DIA + 1 * HORA,
  },
  {
    name: 'email_expirados_d15',
    channel: 'email',
    category: 'utility',
    sector: 'funil',
    audience: 'plano_vencido',
    touch: 'D+15',
    status: 'texto_livre',
    statusNote: null,
    subject: '{primeiro_nome}, seu acesso ao {plano} venceu há 15 dias',
    preheader: 'Seu progresso continua salvo. Voltar não recomeça nada.',
    body: 'Oi, {primeiro_nome}!\nFaz 15 dias que o seu acesso ao {plano} venceu. Seu progresso, suas estatísticas e todo o seu histórico continuam exatamente como você deixou.\nSe voltar agora, você retoma de onde parou.\nEndereço direto: {link}',
    footer: null,
    buttons: [{ kind: 'link', label: 'Reativar meu acesso', url: '{link}' }],
    voice: null,
    companyId: null,
    usedIn: ['Régua expirados D+15 (sai junto do WhatsApp)'],
    base: 120,
    taxas: { entrega: 9_700, leitura: 3_100, clique: 300, resposta: 30, pagamento: 25, vendedora: 0 },
    atualizadoHa: 33 * DIA + 5 * HORA,
  },
  {
    name: 'email_boas_vindas_acesso',
    channel: 'email',
    category: 'utility',
    sector: 'suporte',
    audience: 'pagou_apos_toque',
    touch: 'Após o pagamento',
    status: 'texto_livre',
    statusNote: null,
    subject: 'Seu acesso ao {produto} está liberado',
    preheader: 'Entre, salve este e-mail e comece hoje.',
    body: 'Oi, {primeiro_nome}!\nPagamento confirmado — seu acesso ao {produto} já está liberado.\nEntre pelo botão abaixo com o mesmo e-mail desta mensagem. Se tiver qualquer dificuldade para entrar, responda este e-mail que a equipe resolve.\nEndereço direto: {link}',
    footer: null,
    buttons: [{ kind: 'link', label: 'Acessar agora', url: '{link}' }],
    voice: null,
    companyId: null,
    usedIn: ['Pós-venda de quem voltou pela régua'],
    base: 95,
    taxas: { entrega: 9_800, leitura: 6_100, clique: 4_800, resposta: 150, pagamento: 0, vendedora: 0 },
    atualizadoHa: 27 * DIA + 10 * HORA,
  },
  {
    name: 'email_abandono_turma_nova',
    channel: 'email',
    category: 'marketing',
    sector: 'comercial',
    audience: 'checkout_abandonado',
    touch: null,
    status: 'texto_livre',
    statusNote: null,
    subject: 'Abriu turma nova do {produto}',
    preheader: 'Quem já tinha começado a inscrição entra primeiro.',
    body: 'Oi, {primeiro_nome}!\nVocê chegou a abrir o checkout do {produto} e não concluiu. Abriu uma turma nova, e quem já tinha começado a inscrição entra na frente.\nAs vagas são limitadas. Se fizer sentido pra você, garanta a sua pelo botão abaixo.\nVocê recebe este e-mail porque começou uma compra conosco. Para não receber mais, use o link de descadastro no rodapé.',
    footer: null,
    buttons: [{ kind: 'link', label: 'Garantir minha vaga', url: '{link}' }],
    voice: null,
    companyId: null,
    usedIn: ['Disparo "Abandonaram há mais de 48h"'],
    base: 420,
    taxas: { entrega: 9_600, leitura: 2_100, clique: 200, resposta: 20, pagamento: 15, vendedora: 0 },
    atualizadoHa: 22 * DIA + 8 * HORA,
  },

  // --- Voz · roteiros da IA ----------------------------------------------------
  {
    name: 'voz_carrinho_t30m',
    channel: 'voice',
    category: 'utility',
    sector: 'funil',
    audience: 'checkout_abandonado',
    touch: 'T+30min',
    status: 'texto_livre',
    statusNote: null,
    body: 'Oi, {primeiro_nome}? Aqui é a assistente virtual da equipe do {produto}. Vi que você começou a comprar e o pagamento não concluiu. Tem um minutinho? Posso te ajudar a finalizar agora.',
    footer: null,
    buttons: [],
    voice: {
      opening:
        'Oi, {primeiro_nome}? Aqui é a assistente virtual da equipe do {produto}. Vi que você começou a comprar e o pagamento não concluiu. Tem um minutinho? Posso te ajudar a finalizar agora.',
      objection:
        'Cartão recusado: oferecer PIX ou outro cartão e mandar o link pelo WhatsApp.\nEstá caro: lembrar o parcelamento e o que está incluso, sem inventar condição.\nVai pensar: perguntar o que falta para decidir e oferecer o link por WhatsApp.\nPediu uma pessoa: registrar o pedido; a vendedora retorna pelo WhatsApp e a régua para.\nPediu para não ligar mais: agradecer, encerrar e marcar o lead como descadastrado.',
      closing:
        'Perfeito, {primeiro_nome}. Vou te mandar o link pelo WhatsApp agora, é só tocar e concluir. Qualquer dúvida, responde lá que a equipe te atende. Obrigada e até já!',
    },
    companyId: null,
    usedIn: ['Régua carrinho T+30min (Tier 1)'],
    base: 389,
    taxas: { entrega: 5_900, leitura: null, clique: null, resposta: 400, pagamento: 520, vendedora: 1_500 },
    atualizadoHa: 38 * DIA + 4 * HORA,
  },
  {
    name: 'voz_expirados_reativacao',
    channel: 'voice',
    category: 'utility',
    sector: 'funil',
    audience: 'plano_vencido',
    touch: 'D+15',
    status: 'texto_livre',
    statusNote: 'Disponível para a régua de expirados; nenhum toque de voz ligado hoje.',
    body: 'Oi, {primeiro_nome}? Aqui é a assistente da equipe do {plano}. Seu acesso venceu há alguns dias e o seu progresso continua salvo. Liguei para saber se você quer retomar.',
    footer: null,
    buttons: [],
    voice: {
      opening:
        'Oi, {primeiro_nome}? Aqui é a assistente da equipe do {plano}. Seu acesso venceu há alguns dias e o seu progresso continua salvo. Liguei para saber se você quer retomar.',
      objection:
        'Não está usando: perguntar o que faltou e registrar o motivo.\nEstá caro: informar as opções de plano atuais, sem prometer condição especial.\nJá renovou: agradecer e encerrar sem insistir.',
      closing: 'Combinado, {primeiro_nome}. Te mando o link de reativação pelo WhatsApp. Obrigada pelo tempo e bom retorno!',
    },
    companyId: null,
    usedIn: [],
    base: 0,
    taxas: TAXAS_ZERO,
    atualizadoHa: 15 * DIA + 9 * HORA,
  },
  {
    name: 'voz_comercial_retorno',
    channel: 'voice',
    category: 'utility',
    sector: 'comercial',
    audience: 'respondeu_regua',
    touch: 'Após a resposta',
    status: 'texto_livre',
    statusNote: 'Fora do horário comercial. O crédito continua no toque da régua que provocou a resposta.',
    body: 'Oi, {primeiro_nome}? Aqui é a assistente do comercial. Você respondeu nossa mensagem sobre o {produto} pedindo para tirar uma dúvida e eu liguei para adiantar. Qual é a sua dúvida?',
    footer: null,
    buttons: [],
    voice: {
      opening:
        'Oi, {primeiro_nome}? Aqui é a assistente do comercial. Você respondeu nossa mensagem sobre o {produto} pedindo para tirar uma dúvida e eu liguei para adiantar. Qual é a sua dúvida?',
      objection:
        'Responder só o que está na base de conhecimento do produto.\nPreço especial, prazo de garantia ou algo fora da base: dizer que a vendedora retorna em horário comercial e registrar a pergunta.',
      closing: 'Anotei tudo, {primeiro_nome}. A vendedora te retorna ainda hoje pelo WhatsApp. Se quiser adiantar, o link do pagamento já está lá na conversa. Obrigada!',
    },
    companyId: null,
    usedIn: ['Retorno do comercial fora do horário'],
    base: 45,
    taxas: { entrega: 6_400, leitura: null, clique: null, resposta: 2_200, pagamento: 1_100, vendedora: 10_000 },
    atualizadoHa: 11 * DIA + 7 * HORA,
  },

  // --- Modelos de UMA empresa ------------------------------------------------
  {
    name: 'fluente_carrinho_t1h_professora',
    channel: 'whatsapp_oficial',
    category: 'utility',
    sector: 'funil',
    audience: 'checkout_abandonado',
    touch: 'T+1h',
    status: 'aprovado',
    statusNote: 'Aprovado pela Meta em 03/09/2026 como utility.',
    body: 'Oi, {primeiro_nome}! Aqui é da Escola Fluente. Seu {produto} ficou parado no checkout — está tudo salvo.\nSe quiser tirar uma dúvida com uma professora antes de concluir, é só tocar no segundo botão.',
    footer: RODAPE_SAIR,
    buttons: [
      { kind: 'link', label: 'Finalizar compra', url: '{link}' },
      { kind: 'reply', label: 'Falar com professora' },
    ],
    voice: null,
    companyId: EMPRESA_DEMO_ID,
    usedIn: ['Régua carrinho T+1h · substitui o modelo global'],
    base: 380,
    taxas: { entrega: 9_400, leitura: 8_000, clique: 900, resposta: 1_600, pagamento: 150, vendedora: 6_500 },
    atualizadoHa: 21 * DIA + 6 * HORA,
  },
  {
    name: 'fluente_email_t24h_aula_experimental',
    channel: 'email',
    category: 'utility',
    sector: 'funil',
    audience: 'checkout_abandonado',
    touch: 'T+24h',
    status: 'texto_livre',
    statusNote: null,
    subject: '{primeiro_nome}, que tal uma aula experimental antes de decidir?',
    preheader: 'Seu {produto} continua reservado. Sem compromisso.',
    body: 'Oi, {primeiro_nome}!\nSeu {produto} continua reservado no checkout. Se a dúvida é se o método funciona pra você, a gente resolve do jeito mais simples: uma aula experimental de 20 minutos, sem compromisso.\nSe preferir concluir direto: {link}',
    footer: null,
    buttons: [{ kind: 'link', label: 'Agendar aula experimental', url: '{link}' }],
    voice: null,
    companyId: EMPRESA_DEMO_ID,
    usedIn: ['Régua carrinho T+24h · substitui o modelo global'],
    base: 260,
    taxas: { entrega: 9_700, leitura: 3_400, clique: 520, resposta: 90, pagamento: 55, vendedora: 1_000 },
    atualizadoHa: 21 * DIA + 5 * HORA,
  },
  {
    name: 'fitpro_carrinho_t1h_treino',
    channel: 'whatsapp_oficial',
    category: 'utility',
    sector: 'funil',
    audience: 'checkout_abandonado',
    touch: 'T+1h',
    status: 'aprovado',
    statusNote: 'Aprovado pela Meta em 10/09/2026 como utility.',
    body: 'Oi, {primeiro_nome}! Aqui é da FitPro. Seu {produto} ficou parado no checkout.\nSeu plano de treino já está montado, esperando só a confirmação do pagamento.',
    footer: RODAPE_SAIR,
    buttons: [
      { kind: 'link', label: 'Confirmar pagamento', url: '{link}' },
      { kind: 'reply', label: 'Tenho uma dúvida' },
    ],
    voice: null,
    companyId: 'SIMULADO-empresa-fit',
    usedIn: ['Régua carrinho T+1h · substitui o modelo global'],
    base: 380,
    taxas: { entrega: 9_500, leitura: 8_100, clique: 1_200, resposta: 1_000, pagamento: 280, vendedora: 3_000 },
    atualizadoHa: 14 * DIA + 2 * HORA,
  },
  {
    name: 'fitpro_expirados_d15_treino_pausado',
    channel: 'whatsapp_oficial',
    category: 'utility',
    sector: 'funil',
    audience: 'plano_vencido',
    touch: 'D+15',
    status: 'aprovado',
    statusNote: 'Aprovado pela Meta em 08/09/2026 como utility.',
    body: 'Oi, {primeiro_nome}! Seu {plano} venceu há 15 dias e o seu plano de treino ficou pausado.\nSuas cargas, seu histórico e a sua evolução continuam salvos. Reative pelo botão e retome de onde parou.',
    footer: RODAPE_SAIR,
    buttons: [
      { kind: 'link', label: 'Reativar meu plano', url: '{link}' },
      { kind: 'reply', label: 'Tenho uma dúvida' },
    ],
    voice: null,
    companyId: 'SIMULADO-empresa-fit',
    usedIn: ['Régua expirados D+15 · substitui o modelo global'],
    base: 120,
    taxas: { entrega: 9_400, leitura: 7_400, clique: 900, resposta: 700, pagamento: 130, vendedora: 2_000 },
    atualizadoHa: 16 * DIA + 3 * HORA,
  },
  {
    name: 'fitpro_expirados_desafio_retorno',
    channel: 'whatsapp_oficial',
    category: 'marketing',
    sector: 'comercial',
    audience: 'plano_vencido',
    touch: null,
    status: 'pendente',
    statusNote: 'Enviado à Meta em 24/09/2026 como marketing.',
    body: 'Oi, {primeiro_nome}! Seu {plano} venceu e o treino ficou parado.\nSegunda começa o Desafio de Retorno de 30 dias, para quem quer voltar ao ritmo. Quem reativa participa sem custo extra.',
    footer: RODAPE_MARKETING,
    buttons: [
      { kind: 'link', label: 'Quero participar', url: '{link}' },
      { kind: 'reply', label: 'Tenho uma dúvida' },
    ],
    voice: null,
    companyId: 'SIMULADO-empresa-fit',
    usedIn: ['1 disparo agendado (expirados sem voltar)'],
    base: 0,
    taxas: TAXAS_ZERO,
    atualizadoHa: 3 * HORA,
  },
  {
    name: 'codelab_carrinho_t24h_bootcamp',
    channel: 'whatsapp_evolution',
    category: 'utility',
    sector: 'funil',
    audience: 'checkout_abandonado',
    touch: 'T+24h',
    status: 'texto_livre',
    statusNote: null,
    body: 'Oi, {primeiro_nome}! Aqui é da CodeLab. Sua inscrição no {produto} ficou parada no checkout.\nSe a dúvida é sobre pré-requisitos ou carga horária, me responde aqui que eu explico. Se quiser concluir direto: {link}',
    footer: null,
    buttons: [],
    voice: null,
    companyId: 'SIMULADO-empresa-dev',
    usedIn: ['Régua carrinho T+24h · substitui o modelo global'],
    base: 260,
    taxas: { entrega: 9_700, leitura: null, clique: 1_000, resposta: 1_800, pagamento: 170, vendedora: 4_000 },
    atualizadoHa: 8 * DIA + 5 * HORA,
  },
  {
    name: 'bemviver_carrinho_t1h_acolhimento',
    channel: 'whatsapp_oficial',
    category: 'utility',
    sector: 'funil',
    audience: 'checkout_abandonado',
    touch: 'T+1h',
    status: 'pendente',
    statusNote: 'Empresa em implantação: enviado à Meta em 23/09/2026.',
    body: 'Oi, {primeiro_nome}, aqui é da Bem Viver. Vi que você começou a inscrição no {produto} e não concluiu.\nSem pressa: se quiser conversar antes, toque no segundo botão. Se quiser concluir, o primeiro te leva de volta.',
    footer: 'Responda SAIR para não receber mais mensagens.',
    buttons: [
      { kind: 'link', label: 'Concluir inscrição', url: '{link}' },
      { kind: 'reply', label: 'Quero conversar' },
    ],
    voice: null,
    companyId: 'SIMULADO-empresa-bem',
    usedIn: ['Régua carrinho T+1h (Bem Viver)'],
    base: 0,
    taxas: TAXAS_ZERO,
    atualizadoHa: 1 * DIA + 1 * HORA,
  },
];

// -----------------------------------------------------------------------------
// Gerador
// -----------------------------------------------------------------------------

function fatorDoEscopo(companyId: string | null, template: Definicao): number {
  // Modelo de UMA empresa so conta o fator dela, esteja ou nao filtrada.
  if (template.companyId !== null) {
    const dona = EMPRESAS.find((e) => e.id === template.companyId);
    return dona === undefined ? 0 : fatorDeEnvio(dona);
  }
  if (companyId !== null) {
    const filtrada = EMPRESAS.find((e) => e.id === companyId);
    return filtrada === undefined ? 0 : fatorDeEnvio(filtrada);
  }
  return EMPRESAS.reduce((soma, e) => soma + fatorDeEnvio(e), 0);
}

/** Ticket medio (centavos inteiros) das ofertas ativas de quem o modelo atende. */
function ticketDoEscopo(companyId: string | null, template: Definicao): number {
  const empresas = template.companyId !== null ? [template.companyId] : companyId !== null ? [companyId] : EMPRESAS.filter((e) => e.status === 'ativa').map((e) => e.id);
  const precos = empresas.flatMap((id) => ofertasDaEmpresa(id).filter((o) => o.active).map((o) => o.priceCents));
  if (precos.length === 0) {
    return 0;
  }
  return Math.round(precos.reduce((s, p) => s + p, 0) / precos.length);
}

function medir(def: Definicao, sent: number, pagamentoBps: number, ticketCents: number): TemplatePerformance {
  const t = def.taxas;
  const delivered = pct(sent, t.entrega);
  const credita = publicoGeraCredito(def.audience);
  const fecharam = pct(sent, pagamentoBps);
  const paid = credita ? fecharam : 0;
  return {
    sent,
    delivered,
    opened: t.leitura === null ? null : pct(delivered, t.leitura),
    clicked: t.clique === null ? null : pct(delivered, t.clique),
    replied: pct(delivered, t.resposta),
    paid,
    // Publico sem credito: `paidViaSeller` conta quem fechou com a vendedora depois (informativo, E5).
    paidViaSeller: credita ? pct(paid, t.vendedora) : pct(fecharam, t.vendedora),
    recoveredCents: paid * ticketCents,
  };
}

function versoesDe(def: Definicao, atual: MessageTemplate): readonly TemplateVersion[] {
  const conteudoAtual = conteudoDe(atual);
  const anteriores = (def.anteriores ?? []).map<TemplateVersion>((a, i) => ({
    version: i + 1,
    savedAt: menosMinutos(a.atualizadoHa),
    savedBy: AUTOR_EQUIPE,
    summary: a.resumo,
    status: a.status,
    statusNote: a.statusNote,
    content: { ...conteudoAtual, ...a.conteudo },
  }));
  return [
    ...anteriores,
    {
      version: anteriores.length + 1,
      savedAt: atual.updatedAt,
      savedBy: AUTOR_EQUIPE,
      summary: def.resumoAtual ?? (anteriores.length === 0 ? 'Criado' : 'Versão atual'),
      status: atual.status,
      statusNote: atual.statusNote,
      content: conteudoAtual,
    },
  ];
}

function materializar(def: Definicao, indice: number, query: TemplateQuery): MessageTemplate {
  const { base, taxas, atualizadoHa, anteriores: _anteriores, resumoAtual: _resumo, anterior, variableMap, ...resto } = def;
  const dias = diasDaJanela(query.range);
  const ticket = ticketDoEscopo(query.companyId, def);
  const sent = escalar(base, dias, fatorDoEscopo(query.companyId, def));

  const volumePct = anterior?.volumePct ?? (hashSimulado(def.name) % 31) - 15;
  const pagamentoPct = anterior?.pagamentoPct ?? (hashSimulado(`${def.name}:pagamento`) % 41) - 20;
  const semBase = query.range.from === null;

  const semVersoes: MessageTemplate = {
    ...resto,
    id: `SIMULADO-modelo-${String(indice + 1).padStart(3, '0')}`,
    variableMap: variableMap ?? [],
    variables: extrairVariaveis(def.body, def.subject, def.preheader, def.footer, def.voice?.opening, def.voice?.objection, def.voice?.closing, ...def.buttons.map((b) => b.url)),
    performance: medir(def, sent, taxas.pagamento, ticket),
    previousPerformance: semBase ? null : medir(def, Math.round((sent * (100 + volumePct)) / 100), Math.round((taxas.pagamento * (100 + pagamentoPct)) / 100), ticket),
    versions: [],
    updatedAt: menosMinutos(atualizadoHa),
  };
  return { ...semVersoes, versions: versoesDe(def, semVersoes) };
}

export function simularModelos(query: TemplateQuery): TemplatesResponse {
  // O indice vem da posicao no catalogo, ANTES do filtro: o id de um modelo nao muda conforme a empresa escolhida.
  const items = CATALOGO.map((def, indice) => ({ def, indice }))
    .filter(({ def }) => query.companyId === null || def.companyId === null || def.companyId === query.companyId)
    .map(({ def, indice }) => materializar(def, indice, query));
  const inicio = query.range.from ?? menosMinutos(DIAS_PADRAO * DIA);
  const fim = query.range.to ?? AGORA_SIMULADO.toISOString();
  const duracao = new Date(fim).getTime() - new Date(inicio).getTime();
  return {
    items,
    periodStart: inicio,
    periodEnd: fim,
    previousPeriod: query.range.from === null ? null : { start: new Date(new Date(inicio).getTime() - duracao).toISOString(), end: inicio },
  };
}
